import { Plus, Save, UserMinus, UserPlus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import { useActionsServices, useAgents, useServicesDirection, useStructures } from '@/api/hooks';
import type { ServiceDirection, SituationLigne } from '@/api/types';
import { useSession } from '@/auth/session';
import { Chargement, ErreurBloc, PageHeader, Vide } from '@/components/common';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SelectNatif } from '@/components/ui/select-natif';
import { cn, dateFr, montant } from '@/lib/utils';
import { OngletsAdministration } from './Onglets';

/** Fiche d'un service : libellé, chef de service, lignes rattachées (cases à cocher parmi les lignes de la direction). */
function FicheService({ s, structureCode, exercice, modifiable, agents, lignes }: { s: ServiceDirection; structureCode: string; exercice: number; modifiable: boolean; agents: Array<{ id: string; prenom: string; nom: string; fonction: string }>; lignes: Array<{ id: string; code: string; libelle: string; budgetActualise: number; serviceCode: string | null }> }) {
  const actions = useActionsServices();
  const [libelle, setLibelle] = useState(s.libelle);
  const [cochees, setCochees] = useState<string[]>(s.lignes.map((l) => l.id));
  const [chef, setChef] = useState({ agentId: '', titulaireId: '', debut: '', fin: '' });
  useEffect(() => {
    setLibelle(s.libelle);
    setCochees(s.lignes.map((l) => l.id));
  }, [s]);
  const enregistrer = async () => {
    try {
      await actions.modifier.mutateAsync({ id: s.id, libelle, exercice });
      await actions.rattacher.mutateAsync({ id: s.id, ligneIds: cochees, exercice });
      toast.success(`Service ${s.code} enregistré.`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Enregistrement impossible.');
    }
  };
  const designer = async () => {
    try {
      await actions.designerChef.mutateAsync({ id: s.id, agentId: chef.agentId, titulaireId: chef.titulaireId || undefined, debut: chef.debut || undefined, fin: chef.fin || undefined, exercice });
      toast.success(chef.titulaireId ? `Intérim du chef de service ${s.code} enregistré.` : `Chef du service ${s.code} désigné.`);
      setChef({ agentId: '', titulaireId: '', debut: '', fin: '' });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Désignation impossible.');
    }
  };
  const retirer = async (habilitationId: string, nom: string) => {
    try {
      await actions.retirerChef.mutateAsync({ id: s.id, habilitationId });
      toast.success(`${nom} n'est plus chef du service ${s.code}.`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Retrait impossible.');
    }
  };
  const titulaires = s.chefs.filter((c) => c.enCours && !c.titulaireId);
  const basculerActif = async () => {
    try {
      await actions.modifier.mutateAsync({ id: s.id, actif: !s.actif, exercice });
      toast.success(s.actif ? `Service ${s.code} désactivé.` : `Service ${s.code} réactivé.`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Action impossible.');
    }
  };
  const disponibles = lignes.filter((l) => !l.serviceCode || l.serviceCode === s.code);
  return (
    <Card className={cn(!s.actif && 'opacity-60')}>
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2">
          {structureCode}/{s.code} · {s.libelle}
          {!s.actif && <Badge variant="outline">désactivé</Badge>}
          <Badge variant="secondary">{s.lignes.length} ligne(s) · {montant(s.lignes.reduce((t, l) => t + l.budgetActualise, 0))}</Badge>
        </CardTitle>
        <CardDescription>{s.responsableNom ? `Chef de service : ${s.responsableNom}` : 'Aucun chef de service désigné : le rapport mensuel du service ne sera pas envoyé.'}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {modifiable && (
          <div className="space-y-1.5">
            <Label htmlFor={`libelle-${s.id}`}>Libellé</Label>
            <Input id={`libelle-${s.id}`} value={libelle} onChange={(e) => setLibelle(e.target.value)} maxLength={160} />
          </div>
        )}
        <div>
          <div className="mb-1 text-sm font-medium">Chefs de service (habilitations)</div>
          {s.chefs.length === 0 ? (
            <p className="text-sm text-muted-foreground">Aucune habilitation Chef de service sur ce service.</p>
          ) : (
            <ul className="space-y-1 text-sm">
              {s.chefs.map((c) => (
                <li key={c.habilitationId} className="flex flex-wrap items-center gap-2">
                  <span className={cn(!c.enCours && 'text-muted-foreground')}>{c.nom}</span>
                  {c.titulaireNom && <Badge variant="outline">intérim de {c.titulaireNom}</Badge>}
                  {!c.enCours && <Badge variant="outline">{c.debut && c.debut > new Date().toISOString().slice(0, 10) ? `à partir du ${dateFr(c.debut)}` : 'échue'}</Badge>}
                  {(c.debut || c.fin) && <span className="text-xs text-muted-foreground">{c.debut ? dateFr(c.debut) : '…'} → {c.fin ? dateFr(c.fin) : '…'}</span>}
                  {modifiable && (
                    <Button size="sm" variant="ghost" onClick={() => retirer(c.habilitationId, c.nom)} disabled={actions.retirerChef.isPending} title="Retirer l'habilitation (conservée dans l'historique)">
                      <UserMinus className="size-4" /> Retirer
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
        {modifiable && (
          <div className="grid gap-2 rounded-md border bg-muted/30 p-3 sm:grid-cols-2">
            <div className="space-y-1 sm:col-span-2">
              <Label htmlFor={`chef-${s.id}`}>Désigner un chef de service (agent de la direction)</Label>
              <SelectNatif id={`chef-${s.id}`} value={chef.agentId} onChange={(e) => setChef({ ...chef, agentId: e.target.value })}>
                <option value="">Choisir…</option>
                {agents.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.prenom} {a.nom} · {a.fonction}
                  </option>
                ))}
              </SelectNatif>
            </div>
            {titulaires.length > 0 && (
              <div className="space-y-1 sm:col-span-2">
                <Label htmlFor={`interim-${s.id}`}>Intérim d'un chef titulaire (facultatif, date de fin obligatoire)</Label>
                <SelectNatif id={`interim-${s.id}`} value={chef.titulaireId} onChange={(e) => setChef({ ...chef, titulaireId: e.target.value })}>
                  <option value="">Non : nouveau titulaire (remplace le chef actuel)</option>
                  {titulaires.map((t) => (
                    <option key={t.habilitationId} value={t.agentId}>
                      Intérim de {t.nom}
                    </option>
                  ))}
                </SelectNatif>
              </div>
            )}
            <div className="space-y-1">
              <Label htmlFor={`debut-${s.id}`}>Début</Label>
              <Input id={`debut-${s.id}`} type="date" value={chef.debut} onChange={(e) => setChef({ ...chef, debut: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label htmlFor={`fin-${s.id}`}>Fin {chef.titulaireId ? '(obligatoire pour un intérim)' : ''}</Label>
              <Input id={`fin-${s.id}`} type="date" value={chef.fin} onChange={(e) => setChef({ ...chef, fin: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <Button variant="outline" onClick={designer} disabled={!chef.agentId || actions.designerChef.isPending || (Boolean(chef.titulaireId) && !chef.fin)}>
                <UserPlus className="size-4" /> {chef.titulaireId ? "Enregistrer l'intérim" : 'Désigner'}
              </Button>
            </div>
          </div>
        )}
        <div>
          <div className="mb-1 text-sm font-medium">Lignes rattachées {exercice}</div>
          {disponibles.length === 0 ? (
            <p className="text-sm text-muted-foreground">Aucune ligne disponible : toutes les lignes de la direction sont rattachées à d'autres services.</p>
          ) : (
            <ul className="grid gap-1 sm:grid-cols-2">
              {disponibles.map((l) => (
                <li key={l.id}>
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox checked={cochees.includes(l.id)} disabled={!modifiable} onCheckedChange={(c) => setCochees(c === true ? [...cochees, l.id] : cochees.filter((x) => x !== l.id))} />
                    <span className="font-medium">{l.code}</span>
                    <span className="truncate text-muted-foreground">{l.libelle}</span>
                  </label>
                </li>
              ))}
            </ul>
          )}
        </div>
        {modifiable && (
          <div className="flex flex-wrap gap-2">
            <Button onClick={enregistrer} disabled={actions.modifier.isPending || actions.rattacher.isPending || libelle.trim().length < 2}>
              <Save className="size-4" /> Enregistrer
            </Button>
            <Button variant="ghost" onClick={basculerActif} disabled={actions.modifier.isPending}>
              {s.actif ? 'Désactiver' : 'Réactiver'}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/** Services des directions (paramétrage E7) : rattachement des lignes et chefs de service, saisis par la Direction financière. */
export function ServicesDirections() {
  const session = useSession();
  const profils = session?.utilisateur.profils ?? [];
  const modifiable = profils.some((p) => ['DIRECTEUR_FINANCIER', 'ADMINISTRATEUR'].includes(p));
  const exercice = new Date().getFullYear();
  const structures = useStructures();
  const [structureCode, setStructureCode] = useState('');
  useEffect(() => {
    if (!structureCode && structures.data?.length) setStructureCode(structures.data[0]!.code);
  }, [structures.data, structureCode]);
  const services = useServicesDirection(structureCode || undefined, exercice);
  const agents = useAgents(structureCode || undefined);
  const lignes = useQuery({ queryKey: ['budget', 'lignes', exercice, structureCode] as const, queryFn: () => api<SituationLigne[]>(`/budget/lignes?exercice=${exercice}&structure=${structureCode}`), enabled: Boolean(structureCode) });
  const actions = useActionsServices();
  const [nouveau, setNouveau] = useState({ code: '', libelle: '' });
  const creer = async () => {
    try {
      await actions.creer.mutateAsync({ structureCode, code: nouveau.code.trim().toUpperCase(), libelle: nouveau.libelle.trim(), exercice });
      toast.success(`Service ${nouveau.code.toUpperCase()} créé dans ${structureCode}.`);
      setNouveau({ code: '', libelle: '' });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Création impossible.');
    }
  };
  const lignesDirection = (lignes.data ?? []).map((l) => ({ id: l.id, code: l.code, libelle: l.libelle, budgetActualise: l.budgetActualise, serviceCode: l.serviceCode }));
  return (
    <div>
      <PageHeader
        titre="Administration"
        sousTitre="Services des directions : niveau facultatif entre la direction et ses lignes budgétaires. Chaque service reçoit son rapport mensuel (chef de service en destinataire, directeur en copie) et explique ses écarts avant le directeur."
        actions={
          <SelectNatif value={structureCode} onChange={(e) => setStructureCode(e.target.value)} aria-label="Direction">
            {(structures.data ?? []).map((s) => (
              <option key={s.id} value={s.code}>
                {s.code} · {s.libelle}
              </option>
            ))}
          </SelectNatif>
        }
      />
      <OngletsAdministration />
      {services.isLoading || lignes.isLoading ? (
        <Chargement />
      ) : services.error || !services.data ? (
        <ErreurBloc erreur={services.error} />
      ) : (
        <div className="space-y-4">
          {modifiable && (
            <Card>
              <CardHeader>
                <CardTitle>Nouveau service dans {structureCode}</CardTitle>
                <CardDescription>Code court unique dans la direction (2 à 12 caractères) et libellé ; le chef de service et les lignes se renseignent ensuite sur la fiche.</CardDescription>
              </CardHeader>
              <CardContent className="grid gap-3 sm:grid-cols-[8rem_1fr_auto] sm:items-end">
                <div className="space-y-1.5">
                  <Label htmlFor="nouveau-code">Code</Label>
                  <Input id="nouveau-code" value={nouveau.code} onChange={(e) => setNouveau({ ...nouveau, code: e.target.value })} placeholder="IDV" maxLength={12} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="nouveau-libelle">Libellé</Label>
                  <Input id="nouveau-libelle" value={nouveau.libelle} onChange={(e) => setNouveau({ ...nouveau, libelle: e.target.value })} placeholder="Identification des victimes" maxLength={160} />
                </div>
                <Button onClick={creer} disabled={actions.creer.isPending || nouveau.code.trim().length < 2 || nouveau.libelle.trim().length < 2}>
                  <Plus className="size-4" /> Créer
                </Button>
              </CardContent>
            </Card>
          )}
          {services.data.length === 0 ? (
            <Vide message="Aucun service : les lignes de cette direction sont portées directement par la direction." />
          ) : (
            <div className="grid gap-4 lg:grid-cols-2">
              {services.data.map((s) => (
                <FicheService key={s.id} s={s} structureCode={structureCode} exercice={exercice} modifiable={modifiable} agents={agents.data ?? []} lignes={lignesDirection} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
